/** @typedef {import("../../api/types").Order} Order */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {useParams} from "react-router-dom";
import QrCode from "../../shared/components/QrCode";
import {formatDate, formatMoney} from "../../shared/utils/format";
import {printedProviderName, receiptPlaceAndDate, receiptSentence} from "./utils/orderPrint";
import "./OrderPrint.css";

const ORDERS_URL = "/os/api/service_orders/";
// Confirmed and Done orders: the price is final and nothing was paid yet.
const PAYABLE = ["Confirmed", "Done"];

// fields_content is a JSON-encoded object (string) per the API schema.
const parseFields = (content) => {
    try {
        const parsed = JSON.parse(content || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

/** @param {{device: Order["order_devices"][number]}} props */
const DeviceLine = ({device}) => {
    const filled = Object.entries(parseFields(device.fields_content)).filter(([, value]) => value);
    return (
        <li>
            <strong>{device.device_type?.name || "Sem tipo"}</strong>
            {device.description && `: ${device.description}`}
            {filled.length > 0 && <span className="muted"> ({filled.map(([k, v]) => `${k}: ${v}`).join(" · ")})</span>}
        </li>
    );
};

/**
 * An order on paper, at /orders/:id/print, with nothing but its content: plain
 * black on white, laid out for A4. Below a dashed line is the receipt ("Recibo
 * de Retirada") the provider tears off and has the customer sign when they get
 * their devices and products. The button that starts printing is on screen only.
 */
const OrderPrint = () => {
    const {id} = useParams();
    const [order, setOrder] = useState(/** @type {Order|null} */ (null));
    const [error, setError] = useState(/** @type {string|null} */ (null));
    // The Pix code, for orders whose price is final and unpaid (what the customer can pay).
    const [pix, setPix] = useState(/** @type {string|null} */ (null));

    useEffect(() => {
        const controller = new AbortController();
        setOrder(null);
        setError(null);

        axios
            .get(`${ORDERS_URL}${encodeURIComponent(id)}/`, {signal: controller.signal})
            .then(({data}) => setOrder(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError(
                    err.response?.status === 404
                        ? "Ordem de serviço não encontrada."
                        : "Não foi possível carregar a ordem de serviço."
                );
            });

        return () => controller.abort();
    }, [id]);

    useEffect(() => {
        setPix(null);
        if (!order || !PAYABLE.includes(order.status) || !order.available_actions?.includes("pix")) return undefined;
        const controller = new AbortController();
        axios
            .get(`${ORDERS_URL}${order.id}/pix/`, {signal: controller.signal})
            .then(({data}) => typeof data?.payload === "string" && setPix(data.payload))
            .catch(() => {
                // No key, or not in reais: the paper simply has no QR code.
            });
        return () => controller.abort();
    }, [order]);

    // The tab (and the PDF's file name) says which order it is.
    useEffect(() => {
        if (!order) return undefined;
        const previous = document.title;
        document.title = `Ordem de serviço nº ${order.number}`;
        return () => {
            document.title = previous;
        };
    }, [order]);

    if (error) return <p className="order-print">{error}</p>;
    if (!order) return <p className="order-print">Carregando…</p>;

    // Canceled and expired orders have no paper version.
    if (!order.available_actions?.includes("print")) {
        return <p className="order-print">Esta ordem não pode ser impressa.</p>;
    }

    const {client, currency, order_items: items, order_devices: devices, provider_data: config} = order;
    const products = order.order_products ?? [];
    const discount = Number(order.formatted_absolute_discount) || 0;
    const total = Number(order.total_value);
    const providerName = printedProviderName(config, order.provider);

    return (
        <>
            <div className="order-print-bar">
                <button type="button" onClick={() => window.print()}>
                    Imprimir
                </button>
            </div>
            <main className="order-print">
                <header className="masthead">
                    <div>
                        <h1>{providerName}</h1>
                        {[order.phone, config?.address].filter(Boolean).map((line) => (
                            <p key={line}>{line}</p>
                        ))}
                    </div>
                    <div className="order-id">
                        <h2>Ordem de serviço nº {order.number}</h2>
                        <p>Criada em {formatDate(order.created_at)}</p>
                    </div>
                </header>

                <div className="columns">
                    <section>
                        <h2>Cliente</h2>
                        <p>
                            <strong>{client?.name}</strong>
                        </p>
                        {[client?.phone, client?.alternate_phone, client?.email, client?.address]
                            .filter(Boolean)
                            .map((line) => (
                                <p key={line}>{line}</p>
                            ))}
                    </section>

                    <section>
                        <h2>Equipamentos do cliente</h2>
                        {devices?.length ? (
                            <ul>
                                {devices.map((device, index) => (
                                    <DeviceLine device={device} key={index} />
                                ))}
                            </ul>
                        ) : (
                            <p className="empty">Nenhum equipamento.</p>
                        )}
                    </section>
                </div>

                {order.observations && (
                    <section>
                        <h2>Observações</h2>
                        <p style={{whiteSpace: "pre-wrap"}}>{order.observations}</p>
                    </section>
                )}

                <h2>Serviços</h2>
                {items?.length ? (
                    <table>
                        <thead>
                            <tr>
                                <th>Serviço</th>
                                <th className="num">Qtd.</th>
                                <th className="num">Valor unit.</th>
                                <th className="num">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.service_item?.name}</td>
                                    <td className="num">{item.quantity}</td>
                                    <td className="num">{formatMoney(item.base_value, currency)}</td>
                                    <td className="num">{formatMoney(item.value, currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="empty">Nenhum serviço.</p>
                )}

                <h2>Produtos</h2>
                {products.length ? (
                    <table>
                        <thead>
                            <tr>
                                <th>Produto</th>
                                <th className="num">Qtd.</th>
                                <th className="num">Valor unit.</th>
                                <th className="num">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((line) => (
                                <tr key={line.id}>
                                    <td>{line.product?.name}</td>
                                    <td className="num">{line.quantity}</td>
                                    <td className="num">{formatMoney(line.base_value, currency)}</td>
                                    <td className="num">{formatMoney(line.value, currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="empty">Nenhum produto.</p>
                )}

                <div className="summary">
                    {pix && (
                        <div className="pix">
                            <QrCode value={pix} label="QR code Pix" size={170} />
                            <p>Pague com Pix: leia o QR code com o app do seu banco.</p>
                        </div>
                    )}
                    <table className="totals">
                        <tbody>
                            {discount > 0 && (
                                <>
                                    <tr>
                                        <td>Subtotal</td>
                                        <td className="num">{formatMoney(total + discount, currency)}</td>
                                    </tr>
                                    <tr>
                                        <td>
                                            Desconto{order.discount_description && ` (${order.discount_description})`}
                                        </td>
                                        <td className="num">-{formatMoney(discount, currency)}</td>
                                    </tr>
                                </>
                            )}
                            <tr className="grand-total">
                                <td>Total</td>
                                <td className="num">{formatMoney(total, currency)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="cut" />
                <section className="receipt">
                    <h2>Recibo de Retirada</h2>
                    <p>
                        {receiptSentence(
                            {devices: Boolean(devices?.length), products: products.length > 0},
                            order.number
                        )}
                    </p>
                    {devices?.length || products.length ? (
                        <ul>
                            {devices?.map((device, index) => (
                                <DeviceLine device={device} key={index} />
                            ))}
                            {products.map((line) => (
                                <li key={line.id}>
                                    <strong>Produto</strong>: {line.quantity > 1 && `${line.quantity}× `}
                                    {line.product?.name}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="empty">Nenhum equipamento ou produto nesta ordem.</p>
                    )}
                    <div className="signature">
                        <div>{client?.name}</div>
                        {client?.phone && <div>{client.phone}</div>}
                    </div>
                    <p className="place-date">{receiptPlaceAndDate(config?.city, new Date(), config?.timezone)}</p>
                </section>
            </main>
        </>
    );
};

export default OrderPrint;
